import type { ReactNode } from "react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { find } from "lodash";
import { useLocalStorage } from "@/react/hooks/utils/useLocalStorage";
import { LOCALSTORAGE_KEYS } from "@circle-react/config/Settings";
import { jsonFetchRequest } from "../helpers/backendRequestHelpers";
import { internalApi } from "../helpers/urlHelpers";
import type { SpaceGroup } from "../types";
import type { LoadingStatus } from "./loadingStatuses";
import { ERROR, LOADED, LOADING, REFRESHING } from "./loadingStatuses";

// In long run we may have multiple states for status: loading, reloading, etc.

interface SpaceGroupContextStateType {
  actions: {
    loadData: () => Promise<void>;
    refresh: () => void;
  };
  helpers: {
    isDataLoading: () => boolean;
    isDataRefreshing: () => boolean;
    isDataLoaded: () => boolean;
    findById: (id: string) => SpaceGroup | undefined;
    findBy: (conditions: object) => SpaceGroup | undefined;
  };
  isLoading: boolean;
  records: SpaceGroup[];
  status: LoadingStatus;
}

const initialState: SpaceGroupContextStateType = {
  status: LOADING,
  records: [],
  actions: {
    loadData: () => Promise.resolve(),
    refresh: () => {},
  },
  helpers: {
    isDataLoading: () => false,
    isDataRefreshing: () => false,
    isDataLoaded: () => false,
    findById: () => undefined,
    findBy: () => undefined,
  },
  isLoading: false,
};

const SpaceGroupsContext =
  createContext<SpaceGroupContextStateType>(initialState);
SpaceGroupsContext.displayName = "SpaceGroupsContext";

export interface SpaceGroupsContextProviderProps {
  children: ReactNode;
  spaceGroups?: SpaceGroup[];
}

interface SpaceGroupsApiResponse {
  records: SpaceGroup[];
}

export const SpaceGroupsContextProvider = ({
  children,
  spaceGroups,
}: SpaceGroupsContextProviderProps) => {
  const [status, setStatus] = useState(LOADING);
  const [records, setRecords] = useState<SpaceGroup[]>([]);
  const { getValue: getLocalStorageValue, setValue: setLocalStorageValue } =
    useLocalStorage<SpaceGroup[]>(LOCALSTORAGE_KEYS.SpaceGroupsContextProvider);

  const onDataLoadSuccess = useCallback(
    (data: SpaceGroupsApiResponse) => {
      setRecords(data.records);
      setStatus(LOADED);
      setLocalStorageValue(data.records);
    },
    [setLocalStorageValue],
  );

  const loadData = useCallback(async () => {
    try {
      const data = await jsonFetchRequest<SpaceGroupsApiResponse>(
        internalApi.spaceGroups.index(),
        {
          method: "GET",
        },
      );
      if (data) {
        onDataLoadSuccess(data);
      } else {
        setStatus(ERROR);
      }
    } catch {
      setStatus(ERROR);
    }
  }, [onDataLoadSuccess]);

  useEffect(() => {
    if (spaceGroups) {
      setRecords(spaceGroups);
      setStatus(LOADED);
    } else {
      const localStorageSpaceGroups = getLocalStorageValue();
      if (localStorageSpaceGroups && localStorageSpaceGroups.length > 0) {
        setRecords(localStorageSpaceGroups);
        setStatus(LOADED);
      }
      void loadData();
    }
  }, [spaceGroups, getLocalStorageValue, loadData]);

  const refresh = useCallback(() => {
    setStatus(REFRESHING);
    void loadData();
  }, [loadData]);

  const isDataLoaded = useCallback(() => status === LOADED, [status]);
  const isDataLoading = useCallback(() => status === LOADING, [status]);
  const isDataRefreshing = useCallback(() => status === REFRESHING, [status]);

  // uses: https://lodash.com/docs/4.17.15#find
  const findBy = useCallback(
    (conditions: object = {}): SpaceGroup | undefined => {
      if (!isDataLoaded()) {
        console.warn(`findBy called when status = ${status}.`);
      }
      const result = find(records, conditions);
      if (result && typeof result === "object" && "id" in result) {
        return result;
      }
      return undefined;
    },
    [isDataLoaded, status, records],
  );

  const findById = useCallback(
    (id: string): SpaceGroup | undefined => findBy({ id }),
    [findBy],
  );

  const value: SpaceGroupContextStateType = useMemo(
    () => ({
      status,
      records,
      actions: { loadData, refresh },
      isLoading: isDataLoading(),
      helpers: {
        isDataLoading,
        isDataRefreshing,
        isDataLoaded,
        findById,
        findBy,
      },
    }),
    [
      status,
      records,
      loadData,
      refresh,
      isDataLoading,
      isDataRefreshing,
      isDataLoaded,
      findById,
      findBy,
    ],
  );
  return (
    <SpaceGroupsContext.Provider value={value}>
      {children}
    </SpaceGroupsContext.Provider>
  );
};

export const useSpaceGroupsContext = (): SpaceGroupContextStateType =>
  useContext(SpaceGroupsContext);
